import { useEffect, useState, useMemo } from 'react';
import { getPools } from '../../../../db/dbConnector';
import { Icon } from '@iconify/react';
import { ComplementsContainer } from './complements/ComplementsContainer';
import { FiberViewer3D } from '../../../three/FiberViewer3D';

type Pool = {
    id?: string;
    name?: string;
    category?: string;
    description?: string;
    picture?: string;
    sizes: string[];
    colors?: { name: string; code: string; picture?: string }[];
};

export const FiberDetail = ({ id }: { id: string }) => {
    const [pool, setPool] = useState({ sizes: [] });
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedColor, setSelectedColor] = useState(null);

    const placeholder = '/assets/complements/noimage.svg';

    const goToTopElement = () => {
        const el = document.getElementById('top');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        getPools().then((pools: Pool[]) => {
            const foundPool = pools.find((item) => item.id === id) || { sizes: [] };
            setPool(foundPool);
            setSelectedColor(null);
            setCurrentImageIndex(0);
            goToTopElement();
        });
    }, [id]);

    // ------------- Galería -------------
    const galleryItems = useMemo(() => {
        const items: any[] = [];

        if (pool.picture) {
            items.push({
                type: 'render',
                src: '/' + pool.picture,
                alt: pool.name || 'Render',
                color: null,
                description: 'Vista general',
            });
        }

        items.push({
            type: '3d',
            alt: `Modelo 3D de ${pool.name || ''}`,
            color: selectedColor,
            description: 'Modelo 3D',
        });

        if (pool.colors && pool.colors.length > 0) {
            pool.colors.forEach((color) => {
                if (color.picture) {
                    items.push({
                        type: 'image',
                        src: '/' + color.picture,
                        alt: `${pool.name} - ${color.name}`,
                        color: color.code,
                        description: `Color ${color.name}`,
                        colorName: color.name,
                    });
                }
            });
        }

        return items.length > 0
            ? items
            : [
                {
                    type: 'image',
                    src: placeholder,
                    alt: 'Imagen no disponible',
                    color: null,
                    description: 'Imagen no disponible',
                },
            ];
    }, [selectedColor, pool.picture, pool.name, pool.colors]);

    const currentItem = galleryItems[currentImageIndex];

    // sincroniza el color seleccionado con el índice
    useEffect(() => {
        const col = galleryItems[currentImageIndex]?.color;
        if (col && col !== selectedColor) setSelectedColor(col);
    }, [currentImageIndex, galleryItems, selectedColor]);

    // ------------- UI -------------
    const nextImage = () =>
        setCurrentImageIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));

    const prevImage = () =>
        setCurrentImageIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));

    return (
        <div className="pool__detail" id="top">
            <div className="detail__title">
                <h3>{pool.category}</h3>
                <small>/ {pool.name}</small>
                <button className="btn btn__back" onClick={() => window.history.back()}>
                    Volver...
                </button>
            </div>

            <div className="detail__container">
                <div className="detail__gallery">
                    <button
                        className="gallery__nav gallery__nav--prev"
                        onClick={prevImage}
                        aria-label="Imagen anterior"
                    >
                        <Icon icon="bi:chevron-left" />
                    </button>

                    <div className="gallery__content">
                        {currentItem.type === '3d' ? (
                            <FiberViewer3D modelPath={`/assets/models/${pool.id}.glb`} />
                        ) : currentItem.type === 'render' ? (
                            <img src={currentItem.src} alt={currentItem.alt} className="gallery__image render" />
                        ) : (
                            <img src={currentItem.src} alt={currentItem.alt} className="gallery__image" />
                        )}

                        <div className="gallery__caption">
                            <span>{currentItem.description}</span>
                            {currentItem.colorName && (
                                <span className="caption__color">
                  <Icon icon="bi:palette" />
                                    {currentItem.colorName}
                </span>
                            )}
                        </div>
                    </div>

                    <button
                        className="gallery__nav gallery__nav--next"
                        onClick={nextImage}
                        aria-label="Siguiente imagen"
                    >
                        <Icon icon="bi:chevron-right" />
                    </button>

                    <div className="gallery__indicators">
                        {galleryItems.map((item, index) => (
                            <button
                                key={index}
                                className={`gallery__indicator ${index === currentImageIndex ? 'active' : ''}`}
                                onClick={() => setCurrentImageIndex(index)}
                                aria-label={`Ir a imagen ${index + 1}`}
                                title={item.description}
                            />
                        ))}
                    </div>
                </div>

                <div className="detail__content">
                    <h3>{pool.name}</h3>

                    <div className="detail__colors">
                        <p>Vistas disponibles:</p>
                        <div className="colors__container">
                            <button
                                className={`color__button ${currentImageIndex === 0 ? 'active' : ''}`}
                                onClick={() => setCurrentImageIndex(0)}
                                title="Vista general"
                                aria-label="Ver vista general"
                            >
                                <span className="color__name">General</span>
                            </button>

                            <button
                                className={`color__button color__button--3d ${currentImageIndex === 1 ? 'active' : ''}`}
                                onClick={() => setCurrentImageIndex(1)}
                                title="Ver modelo 3D"
                                aria-label="Ver modelo 3D"
                            >
                                <span className="color__name">3D</span>
                            </button>

                            {pool.colors?.map((color, idx) => {
                                const colorIndex = (pool.picture ? 2 : 1) + idx; // acomoda según si hay render principal
                                return (
                                    <button
                                        key={idx}
                                        className={`color__button ${currentImageIndex === colorIndex ? 'active' : ''}`}
                                        style={{ backgroundColor: color.code }}
                                        onClick={() => setCurrentImageIndex(colorIndex)}
                                        title={`Ver en color ${color.name}`}
                                        aria-label={`Ver en color ${color.name}`}
                                    >
                                        <span className="color__name">{color.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="detail__sizes">
                        <p>Medidas disponibles:</p>
                        <ul>{pool.sizes?.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>

                    <p>{pool.description}</p>

                    <a
                        href={`https://wa.me/5491160140192?text=Hola%20quiero%20presupuesto%20de%20${encodeURIComponent(
                            pool.name || ''
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-budget"
                    >
                        <Icon icon={'bi:whatsapp'} />
                        Solicitar presupuesto
                    </a>
                </div>
            </div>

            <section className="detail__complements">
                <h3>Complementos</h3>
                <ComplementsContainer />
            </section>
        </div>
    );
};
